import React, { FC, useState } from 'react'

import { EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiText, EuiTitle, EuiToolTip } from '@elastic/eui'

import AIPlannerMarkdownRenderer from 'features/planner/components/AIPlannerMarkdownRenderer'
import SparklesIcon from 'features/planner/components/SparklesIcon'

import EditFieldFlyout from './EditFieldFlyout'

interface MarketAnalysisSectionProps {
  sectionName: string
  fields: { key: string; label: string; value: string }[]
  onFieldEdit: (sectionName: string, fieldKey: string, value: string) => void
  onAIEditClick?: (sectionName: string, fieldKey: string, fieldLabel: string, currentValue: string) => void
  onEditTitleClick?: (sectionName: string, fieldKey: string, currentLabel: string) => void
  editMode: boolean
  isUpdating?: boolean
  showBlueprintWarning?: boolean
}

const MarketAnalysisSection: FC<MarketAnalysisSectionProps> = ({ sectionName, fields, onFieldEdit, onAIEditClick, onEditTitleClick, editMode, isUpdating = false, showBlueprintWarning = false }) => {
  const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null)

  const editingField = editingFieldKey ? fields.find(f => f.key === editingFieldKey) : null

  return (
    <>
      {fields.map((field, idx) => (
        <React.Fragment key={field.key}>
          {idx > 0 && <EuiSpacer size='m' />}
          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiTitle size='s'>
                <h3>{field.label}</h3>
              </EuiTitle>
            </EuiFlexItem>
            {editMode && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Edit Title'>
                    <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={() => onEditTitleClick?.(sectionName, field.key, field.label)} aria-label={`Edit Title ${field.label}`} size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='Manual Edit'>
                    <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => setEditingFieldKey(field.key)} aria-label={`Manual Edit ${field.label}`} size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiToolTip content='AI Edit'>
                    <EuiButtonIcon iconType={SparklesIcon} onClick={() => onAIEditClick?.(sectionName, field.key, field.label, field.value || '')} aria-label={`AI Edit ${field.label}`} size='s' />
                  </EuiToolTip>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
          <EuiSpacer size='xs' />
          {field.value ? (
            <AIPlannerMarkdownRenderer content={field.value} />
          ) : (
            <EuiText size='s'>
              <p>{'-'}</p>
            </EuiText>
          )}
        </React.Fragment>
      ))}
      {editingField && (
        <EditFieldFlyout
          fieldKey={editingField.key}
          fieldLabel={editingField.label}
          fieldValue={editingField.value}
          onSave={(fieldKey, value) => {
            onFieldEdit(sectionName, fieldKey, value)
            setEditingFieldKey(null)
          }}
          onClose={() => setEditingFieldKey(null)}
          isUpdating={isUpdating}
          showBlueprintWarning={showBlueprintWarning}
        />
      )}
    </>
  )
}

export default MarketAnalysisSection
